import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { miabApi } from '../api/miab';
import { useAuth } from '../hooks/useAuth';

// Dismissible launch banner for the Marketing in a Box survey, modelled on
// VersionBanner. Two guards keep it honest:
//   • it only renders when there is actually an open, published survey
//     (miabApi.active returns null otherwise), so it can never link to a
//     draft or a 404, and
//   • it retires itself after EXPIRES (end of the launch push) so nobody has
//     to remember to take it down.
// Dismissal is per-browser. The header MIAB badge remains regardless.
const EXPIRES = '2026-07-24'; // inclusive; hidden from 2026-07-25 onward
const DISMISS_KEY = 'amada_miab_banner_dismissed_oct2026';

export default function MiabAnnouncementBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const { data } = useQuery({
    queryKey: ['miab', 'active'],
    queryFn: miabApi.active,
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const today = new Date().toISOString().slice(0, 10);
  if (today > EXPIRES) return null;
  if (dismissed) return null;
  if (!data) return null; // no open survey → nothing to announce

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore storage errors — banner just won't persist dismissal */
    }
    setDismissed(true);
  };

  return (
    <div className="miab-banner" role="status">
      <span className="miab-banner-icon" aria-hidden="true">📦</span>
      <span className="miab-banner-text">
        The <strong>{data.survey.box_label} Marketing in a Box</strong> survey is live —
        tell us what you'd like in the box.{' '}
        <Link to={`/miab/${data.survey.slug}`} className="miab-banner-link">
          Take the survey
        </Link>
      </span>
      <button
        type="button"
        className="miab-banner-close"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
